// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import TableEditor from '../../components/admin/TableEditor.tsx';
import ElementEditor from '../../components/admin/ElementEditor.tsx';
import FloorPlanCanvas from '../../components/admin/FloorPlanCanvas.tsx';
import FloorSelector from '../../components/admin/FloorSelector.tsx';
import FloorTextureSelector from '../../components/admin/FloorTextureSelector.tsx';
import RoomSizeSettings from '../../components/admin/RoomSizeSettings.tsx';
import RoomLayoutSettings from '../../components/admin/RoomLayoutSettings.tsx';
import ElementCreator from '../../components/admin/ElementCreator.tsx';
import { fetchRestaurantSettings } from '../../services/api.ts';
import { usesTableMap } from '../../utils/roomMode.ts';
import '../../styles/floor-plan-editor.css';

const FloorPlanEditor: React.FC = () => {
  const {
    floors,
    currentFloor,
    setCurrentFloor,
  } = useRestaurant();
  const [selectedElement, setSelectedElement] = useState<any>(null);
  const [showTableEditor, setShowTableEditor] = useState(false);
  const [showElementEditor, setShowElementEditor] = useState(false);
  const [showElementCreator, setShowElementCreator] = useState(false);
  const [isEditing] = useState(true);
  const [expandedCard, setExpandedCard] = useState<string>('floorSelector');
  const [legacyMapDefault, setLegacyMapDefault] = useState(true);

  const currentFloorData = floors.find(floor => floor.id === currentFloor);
  const usarMapaMesas = usesTableMap(currentFloorData, legacyMapDefault);
  const hasFloors = floors.length > 0;
  const isLayoutModeEnabled = hasFloors && usarMapaMesas && currentFloorData?.modo_layout !== false;

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        const settings = await fetchRestaurantSettings();
        if (active) {
          setLegacyMapDefault(settings?.reservo_usar_mapa_mesas !== false);
        }
      } catch (error) {
        console.error('Erro ao carregar configuração do mapa de mesas:', error);
      }
    };
    void loadSettings();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isLayoutModeEnabled) {
      setSelectedElement(null);
      setShowTableEditor(false);
      setShowElementEditor(false);
      setShowElementCreator(false);
      setExpandedCard('roomLayoutSettings');
    }
  }, [isLayoutModeEnabled]);

  


  const handleElementSelect = (element: any) => {
    setSelectedElement(element);
    if (element?.type === 'table') {
      setShowTableEditor(true);
      setShowElementEditor(false);
    } else if (element?.type === 'object') {
      setShowElementEditor(true);
      setShowTableEditor(false);
    } else {
      setShowTableEditor(false);
      setShowElementEditor(false);
    }
  };

  const handleCloseElementCreator = () => {
    setShowElementCreator(false);
  };

  const handleToggleCard = (card: string) => {
    setExpandedCard(prev => (prev === card ? '' : card));
  };

  return (
    <div className={`floor-plan-editor ${!isLayoutModeEnabled ? 'layout-disabled' : ''}`}>
      <div className="floor-plan-page-header">
        <div className="floor-plan-page-header-text">
          <h1>Ambientes & Mesas</h1>
          <p>Configure cada ambiente com mapa de mesas ou controle por quantidade de pessoas.</p>
        </div>
      </div>

      <div className={`editor-container ${!isLayoutModeEnabled ? 'layout-disabled' : ''}`}>
        <div className={`editor-sidebar ${!isLayoutModeEnabled ? 'layout-only' : ''}`}>
          {isLayoutModeEnabled && showTableEditor && selectedElement && (
            <TableEditor
              table={selectedElement}
              onSave={() => {
                setShowTableEditor(false);
                setSelectedElement(null);
              }}
            />
          )}

          {isLayoutModeEnabled && showElementEditor && selectedElement && (
            <ElementEditor
              element={selectedElement}
              onSave={() => {
                setShowElementEditor(false);
                setSelectedElement(null);
              }}
            />
          )}

          {!isLayoutModeEnabled && (
            <div className="layout-settings-focus">
              <FloorSelector
                floors={floors}
                currentFloor={currentFloor}
                onChange={setCurrentFloor}
                isCollapsed={false}
                isFocused
                startAddingWhenEmpty
              />

              {hasFloors && (
                <RoomLayoutSettings
                  isCollapsed={false}
                  isFocused
                  usarMapaMesas={usarMapaMesas}
                />
              )}
            </div>
          )}

          {isLayoutModeEnabled && !showTableEditor && !showElementEditor && (
            <>
              <FloorSelector
                floors={floors}
                currentFloor={currentFloor}
                onChange={setCurrentFloor}
                isCollapsed={expandedCard !== 'floorSelector'}
                onToggle={() => handleToggleCard('floorSelector')}
              />

              <FloorTextureSelector
                isCollapsed={expandedCard !== 'textureSelector'}
                onToggle={() => handleToggleCard('textureSelector')}
              />

              <RoomSizeSettings
                isCollapsed={expandedCard !== 'roomSizeSettings'}
                onToggle={() => handleToggleCard('roomSizeSettings')}
              />

              <RoomLayoutSettings
                isCollapsed={expandedCard !== 'roomLayoutSettings'}
                onToggle={() => handleToggleCard('roomLayoutSettings')}
                usarMapaMesas={usarMapaMesas}
              />
            </>
          )}
        </div>

        {isLayoutModeEnabled && (
          <div className="canvas-container">
            <FloorPlanCanvas 
              onElementSelect={handleElementSelect}
              selectedElement={selectedElement}
              isEditing={isEditing}
            />
          </div>
        )}
      </div>

      {isLayoutModeEnabled && showElementCreator && (
        <ElementCreator onClose={handleCloseElementCreator} />
      )}
    </div>
  );
};

export default FloorPlanEditor;
